import { Box, Button, Container, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { authService } from "../services/authService";

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);


  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(120);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setResendStatus("idle");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleResendEmail = async () => {
    if (cooldown > 0) return;
    setResendStatus("sending");
    try {
      await authService.forgotPassword({ email });
      setResendStatus("sent");
      startCooldown();
    } catch {
      setResendStatus("error");
    }
  };

  const handleSubmit = async () => {
    if (!email) return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await authService.forgotPassword({ email });
      setStatus("sent");
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    }
  };

  if (status === "sent") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
        <Container maxWidth="sm">
          <Box sx={{ pt: 16, pb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              align="center"
              sx={{ mb: 3, fontWeight: "normal", fontFamily: 'Nandia' }}
            >
              Forgot Password
            </Typography>
            <Typography
              variant="body1"
              component="p"
              align="center"
              sx={{ mb: 3, fontWeight: "normal" }}
            >
              A password reset link has been sent to your email.
            </Typography>
            <Typography
              variant="body1"
              component="p"
              align="center"
              sx={{ mb: 3, fontWeight: "normal" }}
            >
              {email}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              If you have not received the email, please{" "}
              <Typography
                component="span"
                onClick={handleResendEmail}
                sx={{
                  color:
                    cooldown > 0 ? "text.disabled" : resendStatus === "sent" ? "success.main" : "primary.main",
                  cursor: cooldown > 0 || resendStatus === "sending" ? "default" : "pointer",
                  textDecoration: cooldown > 0 ? "none" : "underline",
                  pointerEvents: cooldown > 0 || resendStatus === "sending" ? "none" : "auto",
                }}
              >
                {resendStatus === "sending"
                  ? "Sending..."
                  : cooldown > 0
                    ? `Resend in ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
                    : "click here"}
              </Typography>{" "}
              to receive a new one.
              {resendStatus === "error" && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Failed to resend email. Please try again.
                </Typography>
              )}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa" }}>
      <Container maxWidth="sm">
        <Box sx={{ pt: 16, pb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{ mb: 3, fontWeight: "normal" }}
          >
            Forgot Password
          </Typography>
          <Typography
            variant="body1"
            component="p"
            align="center"
            sx={{ mb: 3, fontWeight: "normal" }}
          >
            Enter your registered email address to receive a password reset
            link.
          </Typography>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          {status === "error" && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={!email || status === "sending"}
            sx={{
              bgcolor: "#000",
              "&:hover": { bgcolor: "#333" },
              textTransform: "none",
              py: 1.5,
            }}
          >
            Confirm
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
